import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Building2 } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@scio3/ui';
import { CustomerSearchBox } from './CustomerSearchBox';
import { useStaffSearchAccounts } from '../hooks/useStaffCustomer';

/**
 * Accounts column — search by owner email or `@domain`.
 *
 * Rides `users.emails.address_1` in prod (no new index). Dedupe is done
 * server-side so each account appears at most once.
 */
export function AccountsSearchColumn() {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const { data, loading, error } = useStaffSearchAccounts(query);
  const results = data?.staffSearchAccounts ?? [];

  return (
    <section
      className="flex flex-col gap-3 rounded-lg border bg-card p-4"
      data-testid="accounts-search-column"
    >
      <header className="flex items-center gap-2">
        <Building2 className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold text-foreground">{t('Accounts')}</h2>
      </header>
      <CustomerSearchBox
        initialValue={query}
        onSubmit={setQuery}
        placeholder={t('Owner email or @domain')}
        ariaLabel={t('Search accounts')}
        testId="accounts-search-box"
      />
      {error ? (
        <p className="text-xs text-destructive" data-testid="accounts-search-error">
          {t('Search failed.')} {error.message}
        </p>
      ) : loading ? (
        <p className="py-4 text-center text-xs text-muted-foreground">{t('Searching…')}</p>
      ) : query.trim().length < 2 ? (
        <p className="py-4 text-center text-xs text-muted-foreground">{t('Type to search…')}</p>
      ) : results.length === 0 ? (
        <p className="py-4 text-center text-xs text-muted-foreground">{t('No results.')}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('Company')}</TableHead>
              <TableHead>{t('Email')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {results.map((r) => (
              <TableRow
                key={r.accountId}
                className="hover:bg-accent/50 transition-colors"
                data-testid="accounts-search-result-row"
              >
                <TableCell className="font-medium">
                  <Link
                    to={`/staff/customers/${encodeURIComponent(r.accountId)}/devices`}
                    className="hover:underline"
                  >
                    {r.companyName ?? '—'}
                  </Link>
                  <div className="font-mono text-[10px] text-muted-foreground">{r.accountId}</div>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {r.companyEmail ?? '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
}
